import PropTypes from 'prop-types';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import ChevronLeft from '@icon/ChevronLeft';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { getBrowseCountLabel } from '../testTab/browseSections';

const panelIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(16px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const SelectedPanel = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  animation: ${panelIn} 0.32s ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  ${mediaQuery.uptoMobile} {
    flex: 1;
    min-height: 0;
    gap: 0;
    animation: none;
  }
`;

const CardsScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: ${({ $hasPitch }) => ($hasPitch ? '128px' : '0')};

  ${mediaQuery.uptoMobile} {
    -webkit-overflow-scrolling: touch;
  }
`;

const SectionChrome = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: ${Spacings.SPACING_4B};
  padding: ${Spacings.SPACING_4B};
  border: 1px solid var(--color-accent-blue);
  border-radius: 12px;
  background: var(--color-base-1);

  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const MobileHeader = styled.div`
  display: none;

  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: ${Spacings.SPACING_2B};
    padding: ${Spacings.SPACING_4B};
    background: var(--color-i-test-hero);
  }
`;

const MobileHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
`;

const BackButton = styled.button`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--color-i-white);
    outline-offset: 2px;
  }
`;

const MobileHeading = styled.h1`
  margin: 0;
  font-size: 18px;
  line-height: 24px;
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-i-white);
`;

const MobileDescription = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: var(--color-i-test-hero-text);
`;

const MobileSheet = styled.div`
  display: none;

  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    gap: ${Spacings.SPACING_4B};
    padding: ${Spacings.SPACING_4B};
    border-radius: 16px 16px 0 0;
    background: var(--color-base-0);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${Spacings.SPACING_3B};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${Spacings.SPACING_2B};
`;

const SectionIcon = styled.img`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const SectionIconFallback = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: var(--color-blue-bg);
`;

const SectionHeading = styled.h2`
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-text-primary);
`;

const CountLabel = styled.span`
  font-size: 12px;
  line-height: 16px;
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-accent-blue);
  white-space: nowrap;
`;

const SectionDescription = styled.p`
  margin: ${Spacings.SPACING_1B} 0 0;
  font-size: 13px;
  line-height: 18px;
  color: var(--color-text-secondary);
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-text-secondary);

  &:focus-visible {
    outline: 2px solid var(--color-accent-blue);
    outline-offset: 2px;
  }
`;

const TestTabV2SelectedPanel = ({
  section,
  toggle,
  filters,
  hasPitch,
  onClose,
  children
}) => (
  <SelectedPanel>
    <MobileHeader>
      <MobileHeaderRow>
        <BackButton type="button" aria-label="Back" onClick={onClose}>
          <ChevronLeft size="20px" color="var(--color-i-white)" />
        </BackButton>
        <MobileHeading>{section?.heading}</MobileHeading>
      </MobileHeaderRow>
      {section?.description ? (
        <MobileDescription>{section.description}</MobileDescription>
      ) : null}
    </MobileHeader>
    <SectionChrome>
      <SectionHeader>
        <div>
          <TitleRow>
            {section?.iconUrl ? (
              <SectionIcon src={section.iconUrl} alt="" />
            ) : (
              <SectionIconFallback aria-hidden />
            )}
            <SectionHeading>{section?.heading}</SectionHeading>
            {section ? (
              <CountLabel>{getBrowseCountLabel(section)}</CountLabel>
            ) : null}
          </TitleRow>
          {section?.description ? (
            <SectionDescription>{section.description}</SectionDescription>
          ) : null}
        </div>
        <CloseButton type="button" onClick={onClose}>
          Close
        </CloseButton>
      </SectionHeader>
      {filters}
    </SectionChrome>
    <MobileSheet>
      {toggle}
      {filters}
    </MobileSheet>
    <CardsScroll $hasPitch={hasPitch}>{children}</CardsScroll>
  </SelectedPanel>
);

export default TestTabV2SelectedPanel;

TestTabV2SelectedPanel.propTypes = {
  section: PropTypes.object,
  toggle: PropTypes.node,
  filters: PropTypes.node,
  hasPitch: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node
};

TestTabV2SelectedPanel.defaultProps = {
  section: null,
  toggle: null,
  filters: null,
  hasPitch: false,
  children: null
};
